import React from 'react';
import { 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Divider, 
    IconButton 
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";

import InfoHover from 'modules/common/components/hovers/infoHover';
import ToAudienceSelection from 'modules/sms/toAudienceSelection';
import { Audience } from 'modules/audiences';
import { smsApi } from 'api/instances';
import { IdNamePair } from 'modules/common/models';
import AudienceFooter from './audienceFooter';

import "./styles/smsManagement.sass";

interface ISMSOptInDialogProps {
    open: boolean;
    onClose: () => void;
    onNext: () => void;
    audiences: Audience[];
    selectedAudiences: IdNamePair<string, string>[];
    selectAllUsers: boolean;
    setSelectAllUsers: (toSet: boolean) => void;
    setSelectedAudiences: (audiences: IdNamePair<string, string>[]) => void;
    audienceUsers: number;
    setAudienceUsers: (users: number) => void;
    totalUsers: number;
    setTotalUsers: (users:number) => void;
}

const SMSOptInDialog: React.FC<ISMSOptInDialogProps> = ({
    open,
    onClose,
    audiences,
    selectedAudiences,
    setSelectedAudiences,
    onNext,
    audienceUsers,
    setAudienceUsers,
    totalUsers,
    setTotalUsers,
    selectAllUsers,
    setSelectAllUsers
}) => {
    const [fetchingAudienceUsers, setFetchingAudienceUsers] = React.useState(false);

    const updateAudienceTotals = async(audiences?: IdNamePair<string, string>[]) => {
        const smsAudiences = audiences ? audiences.map(a => a.id) : selectedAudiences.map(a => a.id);

        if(smsAudiences.length === 0 && selectAllUsers === false) {
            setAudienceUsers(0);
            setTotalUsers(0);
            return;
        }

        setFetchingAudienceUsers(true);

        const currentTotalsFetch = await smsApi.getAudienceTotals(smsAudiences, selectAllUsers, false);
        const totals = currentTotalsFetch.data;

        setAudienceUsers(totals.audienceOptedUsers);
        setTotalUsers(totals.audienceTotalUsers);
        setFetchingAudienceUsers(false);
    }

    const updateSelectedAudiences = (audiences: IdNamePair<string, string>[]) => {
        setSelectedAudiences(audiences);
    }

    const onChipRemove = (audiences: IdNamePair<string, string>[]) => {
        setSelectedAudiences(audiences);

        //We pass here because the set operation is too slow.
        updateAudienceTotals(audiences);
    }

    const audienceFooter = <AudienceFooter 
        selectAllChecked={selectAllUsers} 
        setSelectAll={setSelectAllUsers} 
        clearAll={() => updateSelectedAudiences([])}
        buttonClassName={"opt-in-footer-clear-all-button"}
        containerClassName={"opt-in-audience-footer"}
        checkBoxId={"send-to-all-employees-checkbox-opt-in-dialog"}
    />

    return (
        <Dialog open={open} className="sms-management-opt-in-dialog">
            <DialogTitle className="opt-in-title">
                <div className="info-icon">
                    Send SMS Opt-In Invite Now
                    <InfoHover style={{verticalAlign: "top"}}>
                        Send a one-time SMS opt-in invitation to specific audiences. 
                        This is useful for inviting existing audiences without enabling automatic invites for new users.
                    </InfoHover>
                </div>
                <div className="opt-in-actions">
                    <IconButton onClick={onClose} size="large">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <Divider/>

            <DialogContent>
                <ToAudienceSelection 
                    isFetching={fetchingAudienceUsers}
                    currentUsers={audienceUsers}
                    totalAudienceUsers={totalUsers}
                    emptyChipText={selectAllUsers ? "All users" : "No recipients selected"}
                    disableOptions={selectAllUsers}
                    audienceFooter={audienceFooter}
                    defaultOptions={audiences.map(a => ({ id: a.id, name: a.displayName }))}
                    selectedOptions={selectAllUsers ? [] : selectedAudiences}
                    onClose={updateAudienceTotals}
                    onChange={(audiences) => updateSelectedAudiences(audiences)}
                    onChipRemove={(audiences) => onChipRemove(audiences)}
                    sendToAllUsers={selectAllUsers}
                    audienceIds={selectedAudiences.map(a => a.id)}
                    filterByOptedIn={false}
                    fileName="send-invite-to.csv"
                />
            </DialogContent>

            <DialogActions>
                <Button variant="text" color="primary" onClick={onClose} id="sms-opt-in-cancel-button">Cancel</Button>
                <Button variant="contained" color="primary" onClick={onNext} disabled={selectedAudiences.length === 0 && !selectAllUsers} id="sms-opt-in-next-button">Next</Button>
            </DialogActions>
        </Dialog>
    )
}

export default SMSOptInDialog;